import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Box, VStack, Text } from '@chakra-ui/react';
import { RubricQuestionField } from '../highlight-drawer/rubric-question-field';
import { findAnswerForQuestion, hasRealAnswer } from '../highlight-drawer/rubric-utils';

export interface AnswerSummaryItem {
  questionId: number;
  label: string;
  answer: string;
}

/** Resolves a human-readable answer display for a question's saved attribute. */
export function getAnswerDisplayText(
  wq: CQA.WorkflowQuestion,
  attribute: CQA.Attribute | undefined
): string {
  if (!attribute || !hasRealAnswer(attribute)) return '';
  const { questionType, answers } = wq.question;
  if (questionType === 'FREE_TEXT') return attribute.answerDisplayText || '';
  if (questionType === 'SPAN' || questionType === 'SPANS') {
    const count = attribute.spanAnnotationIds?.length ?? 0;
    return `${count} span${count !== 1 ? 's' : ''}`;
  }
  if (attribute.answerValueId != null) {
    const match = answers.find(a => a.id === attribute.answerValueId);
    return match?.displayText || match?.name || '';
  }
  return '';
}

/**
 * Returns a summary for every answered question in the workflow.
 * Used to populate collapsed code/case row displays (CDMX-15251).
 */
export function buildAllAnswerSummaries(
  visibleQuestions: CQA.WorkflowQuestion[],
  attributes: CQA.Attribute[] | undefined
): AnswerSummaryItem[] {
  const results: AnswerSummaryItem[] = [];
  for (const wq of visibleQuestions) {
    const attr = findAnswerForQuestion(attributes, wq.question.id);
    if (!hasRealAnswer(attr)) continue;
    const answer = getAnswerDisplayText(wq, attr);
    if (answer) {
      results.push({ questionId: wq.question.id, label: wq.question.name, answer });
    }
  }
  return results;
}

export interface WorkflowWizardProps {
  visibleQuestions: CQA.WorkflowQuestion[];
  attributes: CQA.Attribute[];
  /** The parent's answer-change handler (includes downstream cleanup). */
  onAnswerChange: (questionId: number, value: number | number[] | string) => void;
  /** Optional: called when "Start over" is clicked. */
  onReset?: () => void;
  /** Ref that receives the reset function so parents can trigger reset externally. */
  resetRef?: React.MutableRefObject<(() => void) | null>;
  isDisabled: boolean;
  isLoading: boolean;
  autofilledAnswersMap: Map<number, number[]>;
  filteredAnswersMap: Map<number, number[]>;
  spans: Accelerate.SpanAnnotation[];
  lastChangedQuestionId?: number | null;
}

/**
 * Renders a workflow's questions one at a time (CDMX-15251).
 *
 * - Previously answered questions appear as compact "{question name} — {answer}" summaries.
 * - Only the active (current) question is interactive.
 * - Answering auto-advances to the next question.
 * - "Start over" resets the view to question 1.
 */
export const WorkflowWizard: React.FC<WorkflowWizardProps> = ({
  visibleQuestions,
  attributes,
  onAnswerChange,
  onReset,
  resetRef,
  isDisabled,
  isLoading,
  autofilledAnswersMap,
  filteredAnswersMap,
  spans,
  lastChangedQuestionId,
}) => {
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);

  const autoIndexFloorRef = useRef(0);
  
  const activeIndex = useMemo(() => {
    if (visibleQuestions.length === 0) return 0;
    if (activeQuestionId !== null) {
      const idx = visibleQuestions.findIndex(wq => wq.question.id === activeQuestionId);
      if (idx >= 0) return idx;
    }
    const firstUnanswered = visibleQuestions.findIndex(
      wq => !hasRealAnswer(findAnswerForQuestion(attributes, wq.question.id))
    );
    const computed = firstUnanswered >= 0 ? firstUnanswered : visibleQuestions.length;
    if (computed === 0 && autoIndexFloorRef.current > 0) {
      return Math.min(autoIndexFloorRef.current, visibleQuestions.length);
    }
    return computed;
  }, [visibleQuestions, attributes, activeQuestionId]);

  useEffect(() => {
    if (activeQuestionId === null) {
      autoIndexFloorRef.current = Math.max(
        autoIndexFloorRef.current,
        Math.min(activeIndex, visibleQuestions.length)
      );
    }
  }, [activeIndex, activeQuestionId, visibleQuestions.length]);

  const handleAnswerAndAdvance = useCallback(
    (questionId: number, value: number | number[] | string, questionType: string) => {
      if (isDisabled) return;
      onAnswerChange(questionId, value);
      if (Array.isArray(value) && questionType === 'SPANS') {
        setActiveQuestionId(questionId);
      } else {
        autoIndexFloorRef.current = 0;
        setActiveQuestionId(null);
      }
    },
    [isDisabled, onAnswerChange]
  );

  const handleMultiSelectDone = useCallback(() => {
    autoIndexFloorRef.current = 0;
    setActiveQuestionId(null);
  }, []);

  const handleReset = useCallback(() => {
    autoIndexFloorRef.current = 0;
    if (visibleQuestions.length > 0) {
      setActiveQuestionId(visibleQuestions[0].question.id);
    }
    onReset?.();
  }, [visibleQuestions, onReset]);

  // Expose reset to parent so they can place the button externally.
  if (resetRef) resetRef.current = handleReset;

  const previousQuestions = visibleQuestions.slice(0, activeIndex);
  const activeQuestion =
    activeIndex < visibleQuestions.length ? visibleQuestions[activeIndex] : null;

  return (
    <VStack spacing={2} align="stretch">
      {/* Previously answered questions — clickable two-line summaries */}
      {previousQuestions.map(wq => {
        const attr = findAnswerForQuestion(attributes, wq.question.id);
        const answerText = getAnswerDisplayText(wq, attr);
        return (
          <Box
            key={wq.question.id}
            px={2}
            py="6px"
            borderRadius="sm"
            bg="whiteAlpha.600"
            border="1px solid"
            borderColor="gray.200"
            fontSize="xs"
            color="gray.600"
            lineHeight="1.3"
            cursor="pointer"
            onClick={() => {
              // Only gate re-editing when a work item status is supplied (code
              // bench). WorkflowWizard is also reused for case-level questions
              // and the span drawer, which pass no status and must stay editable.
              if (isDisabled) return;
              setActiveQuestionId(wq.question.id);
            }}
            _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
          >
            <Text fontWeight="medium" color="gray.700">{wq.question.questionText}</Text>
            {answerText && (
              <Text color="gray.500" mt="2px">{answerText}</Text>
            )}
          </Box>
        );
      })}

      {/* Active (current) question */}
      {activeQuestion && (
        <RubricQuestionField
          workflowQuestion={activeQuestion}
          currentAnswer={findAnswerForQuestion(attributes, activeQuestion.question.id)}
          onAnswerChange={value => handleAnswerAndAdvance(activeQuestion.question.id, value, activeQuestion.question.questionType)}
          onDone={handleMultiSelectDone}
          isDisabled={isDisabled || isLoading}
          isAutofilled={autofilledAnswersMap.has(activeQuestion.question.id)}
          filteredAnswerIds={filteredAnswersMap.get(activeQuestion.question.id)}
          isEvaluating={isLoading && lastChangedQuestionId === activeQuestion.question.id}
          spans={spans}
        />
      )}
    </VStack>
  );
};

export default WorkflowWizard;
